import { z } from 'zod';
import { CHECKLIST_ASSIGNEES, CHECKLIST_ITEM_STATUSES, CHECKLIST_KINDS, CHECKLIST_MATCH_RULES, CHECKLIST_STATUSES, STAFF_ROLES } from '@bulk/types';
import { dateOnlySchema, idSchema, patchOf } from './common';

const localizedSchema = z.object({ en: z.string(), ka: z.string() });

const nameSchema = z
  .object({ en: z.string().trim().max(120).default(''), ka: z.string().trim().max(120).default('') })
  .refine((t) => t.en !== '' || t.ka !== '', { message: 'Give the name in at least one language' });

const itemTitleSchema = z
  .object({ en: z.string().trim().max(200).default(''), ka: z.string().trim().max(200).default('') })
  .refine((t) => t.en !== '' || t.ka !== '', { message: 'Give the step in at least one language' });

export const MAX_CHECKLIST_ITEMS = 60;

export const createChecklistTemplateSchema = z.object({
  kind: z.enum(CHECKLIST_KINDS),
  name: nameSchema,
  departmentId: idSchema.nullable().default(null).describe('With neither a department nor a role the template is the gym default'),
  role: z.enum(STAFF_ROLES).nullable().default(null).describe('The gym role of the employee’s login; an employee with only a trainer profile counts as a TRAINER'),
  active: z.boolean().default(true),
});
export type CreateChecklistTemplateInput = z.infer<typeof createChecklistTemplateSchema>;

export const updateChecklistTemplateSchema = patchOf(createChecklistTemplateSchema.omit({ kind: true }));
export type UpdateChecklistTemplateInput = z.infer<typeof updateChecklistTemplateSchema>;

export const checklistTemplateItemInputSchema = z.object({
  title: itemTitleSchema,
  assignee: z.enum(CHECKLIST_ASSIGNEES).default('EMPLOYEE'),
  articleId: idSchema.nullable().default(null).describe('A knowledge-base article the step points to'),
  dueAfterDays: z.number().int().min(0).max(365).nullable().default(null).describe('Days after the checklist starts; empty means no due date'),
});
export type ChecklistTemplateItemInput = z.infer<typeof checklistTemplateItemInputSchema>;

export const replaceChecklistTemplateItemsSchema = z.object({ items: z.array(checklistTemplateItemInputSchema).max(MAX_CHECKLIST_ITEMS) });
export type ReplaceChecklistTemplateItemsInput = z.infer<typeof replaceChecklistTemplateItemsSchema>;

export const checklistArticleSchema = z.object({ id: idSchema, title: localizedSchema });
export type ChecklistArticleDto = z.infer<typeof checklistArticleSchema>;

export const checklistTemplateItemSchema = z.object({
  id: idSchema,
  sortOrder: z.number().int(),
  title: localizedSchema,
  assignee: z.enum(CHECKLIST_ASSIGNEES),
  articleId: idSchema.nullable(),
  article: checklistArticleSchema.nullable().describe('Null when the article is gone or the caller may not open it'),
  dueAfterDays: z.number().int().nullable(),
});
export type ChecklistTemplateItemDto = z.infer<typeof checklistTemplateItemSchema>;

export const checklistTemplateSchema = z.object({
  id: idSchema,
  kind: z.enum(CHECKLIST_KINDS),
  name: localizedSchema,
  departmentId: idSchema.nullable(),
  departmentName: z.string().nullable(),
  role: z.enum(STAFF_ROLES).nullable(),
  active: z.boolean(),
  items: z.array(checklistTemplateItemSchema),
  openChecklists: z.number().int().describe('People working through a copy of it now'),
  updatedAt: z.string(),
});
export type ChecklistTemplateDto = z.infer<typeof checklistTemplateSchema>;

export const assignChecklistSchema = z.object({
  templateId: idSchema.optional().describe('A template to give; left out, the most specific active template of the kind that fits the employee is used'),
  kind: z.enum(CHECKLIST_KINDS).default('ONBOARDING').describe('Only read when no template is named'),
  startsOn: dateOnlySchema.optional().describe('The day due dates count from; onboarding defaults to the employee’s start date, offboarding to today'),
});
export type AssignChecklistInput = z.infer<typeof assignChecklistSchema>;

export const skipChecklistItemSchema = z.object({ reason: z.string().trim().min(1).max(500) });
export type SkipChecklistItemInput = z.infer<typeof skipChecklistItemSchema>;

export const checklistProgressSchema = z.object({
  total: z.number().int(),
  done: z.number().int(),
  skipped: z.number().int(),
  pending: z.number().int(),
  overdue: z.number().int().describe('Pending steps whose due date has passed in the gym’s time zone'),
});
export type ChecklistProgressDto = z.infer<typeof checklistProgressSchema>;

export const checklistItemSchema = z.object({
  id: idSchema,
  sortOrder: z.number().int(),
  title: localizedSchema,
  assignee: z.enum(CHECKLIST_ASSIGNEES),
  status: z.enum(CHECKLIST_ITEM_STATUSES),
  dueOn: dateOnlySchema.nullable(),
  overdue: z.boolean(),
  doneAt: z.string().nullable(),
  doneByName: z.string().nullable(),
  skipReason: z.string().nullable(),
  article: checklistArticleSchema.nullable(),
  canTick: z.boolean().describe('Whether the caller may tick this step or take the tick back'),
  canSkip: z.boolean(),
});
export type ChecklistItemDto = z.infer<typeof checklistItemSchema>;

export const checklistSummarySchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  employeeName: z.string(),
  position: z.string().nullable(),
  departmentName: z.string().nullable(),
  kind: z.enum(CHECKLIST_KINDS),
  name: localizedSchema,
  templateId: idSchema.nullable(),
  matchRule: z.enum(CHECKLIST_MATCH_RULES),
  startsOn: dateOnlySchema,
  status: z.enum(CHECKLIST_STATUSES),
  completedAt: z.string().nullable(),
  assignedByName: z.string().nullable().describe('Null when the checklist was given automatically as the employee was added'),
  progress: checklistProgressSchema,
  nextDueOn: dateOnlySchema.nullable().describe('The earliest due date among the steps still pending'),
});
export type ChecklistSummaryDto = z.infer<typeof checklistSummarySchema>;

export const checklistSchema = checklistSummarySchema.extend({ items: z.array(checklistItemSchema) });
export type ChecklistDto = z.infer<typeof checklistSchema>;

export const checklistOverviewQuerySchema = z.object({
  status: z.enum([...CHECKLIST_STATUSES, 'ALL']).default('OPEN'),
  kind: z.enum(CHECKLIST_KINDS).optional(),
  departmentId: idSchema.optional(),
  overdue: z.stringbool().optional().describe('Only checklists with at least one overdue step'),
});
export type ChecklistOverviewQuery = z.infer<typeof checklistOverviewQuerySchema>;
